// Local tools (§6.2 passes 1, 5, 11–13; §11.4, §11.8, §11.10, §11.11).
//  · STROKE: rasterizes brush strokes into a mask texture that covers the whole source (one channel per
//    brush; add = MAX blending, erase = multiply by 1 − coverage).
//  · MASK_FNS: per-pixel weight of a local mask (linear / radial analytic, brush from the raster), shared
//    by the colour shader and the mask overlay.
//  · HEAL: one heal/clone spot on a linear source texture (patch composited over the source, pass 1).
//  · SKIN_BLUR / SKIN_COMBINE: frequency separation for the skin brush (pass 13).
//  · MASK_OVERLAY: red 50% overlay of one mask (O).
import { HEADER } from './common';

/** Stroke coverage: distance to a polyline of up to 64 points (x, y in mask pixels, z = pressure). */
export const STROKE = `${HEADER}
uniform vec3 uPts[64];
uniform int uN;
uniform float uR;        // radius in mask pixels at full pressure
uniform float uFeather;  // 0..1
uniform float uFlow;     // 0..1
out vec4 o;
void main() {
  vec2 p = gl_FragCoord.xy;
  float cov = 0.0;
  for (int i = 0; i < 64; i++) {
    if (i >= uN) break;
    vec3 a = uPts[i];
    vec3 b = i + 1 < uN ? uPts[i + 1] : a;
    vec2 ab = b.xy - a.xy;
    float t = dot(ab, ab) > 1e-9 ? clamp(dot(p - a.xy, ab) / dot(ab, ab), 0.0, 1.0) : 0.0;
    float d = length(p - (a.xy + t * ab));
    float r = max(uR * max(mix(a.z, b.z, t), 0.1), 0.5);
    float inner = r * (1.0 - uFeather);
    float c = d <= inner ? 1.0 : d >= r ? 0.0 : 1.0 - smoothstep(inner, r, d);
    cov = max(cov, c);
    if (uN == 1) break;
    if (i + 2 >= uN) break;
  }
  o = vec4(cov * uFlow);
}`;

/**
 * Local mask weights. Geometry is in source pixels (uSrcDim = full source size) so ellipses stay round
 * at any preview size. Kinds: 0 linear (full at A.xy, zero at A.zw), 1 radial (centre A.xy, semi-axes
 * A.zw in normalized width/height units, B.x = angle in radians, B.y = feather), 2 brush (B.z = raster
 * channel 0..7 across uBrush0 / uBrush1), 3 M22 colour / object (B.z = channel 0..7 across uPick0 /
 * uPick1, baked by PICK_MASK).
 */
export const MASK_FNS = `
uniform int uMaskN;
uniform int uMaskKind[8];
uniform vec4 uMaskA[8];
uniform vec4 uMaskB[8];
uniform int uMaskInv[8];
uniform sampler2D uBrush0;
uniform sampler2D uBrush1;
uniform sampler2D uPick0;
uniform sampler2D uPick1;
uniform vec2 uSrcDim;
float smoothMask(float e0, float e1, float x) { float t = clamp((x - e0) / max(e1 - e0, 1e-6), 0.0, 1.0); return t * t * (3.0 - 2.0 * t); }
float maskW(int i, vec2 uv) {
  vec2 p = uv * uSrcDim;
  int k = uMaskKind[i];
  float w;
  if (k == 0) {
    vec2 a = uMaskA[i].xy * uSrcDim, b = uMaskA[i].zw * uSrcDim;
    vec2 d = b - a;
    float t = dot(p - a, d) / max(dot(d, d), 1e-6);
    w = 1.0 - smoothMask(0.0, 1.0, t);
  } else if (k == 1) {
    vec2 c = uMaskA[i].xy * uSrcDim;
    vec2 ax = max(uMaskA[i].zw * uSrcDim, vec2(1e-3));
    float cs = cos(uMaskB[i].x), sn = sin(uMaskB[i].x);
    vec2 q = p - c;
    q = vec2(cs * q.x + sn * q.y, -sn * q.x + cs * q.y);
    float d = length(q / ax);
    float fe = clamp(uMaskB[i].y, 0.0, 1.0);
    w = d <= 1.0 - fe ? 1.0 : d >= 1.0 ? 0.0 : 1.0 - smoothMask(1.0 - fe, 1.0, d);
  } else if (k == 3) {
    int ch = int(uMaskB[i].z + 0.5);
    vec4 t = ch < 4 ? texture(uPick0, uv) : texture(uPick1, uv);
    int cc = ch < 4 ? ch : ch - 4;
    w = cc == 0 ? t.r : cc == 1 ? t.g : cc == 2 ? t.b : t.a;
  } else {
    int ch = int(uMaskB[i].z + 0.5);
    vec4 t = ch < 4 ? texture(uBrush0, uv) : texture(uBrush1, uv);
    int cc = ch < 4 ? ch : ch - 4;
    w = cc == 0 ? t.r : cc == 1 ? t.g : cc == 2 ? t.b : t.a;
  }
  w = clamp(w, 0.0, 1.0);
  return uMaskInv[i] == 1 ? 1.0 - w : w;
}
`;

/**
 * One heal / clone spot. The target covers part of the source: source pixel = uMap.xy + fragCoord * uMap.zw.
 * Heal: out = src + G(dst) − G(src), with G a Gaussian (σ = r/2) that skips pixels inside the stroke
 * (DEVIATION: a plain Gaussian of the destination would blur the defect itself back in).
 */
export const HEAL = `${HEADER}
uniform sampler2D uSrc;
uniform vec2 uTexSize;
uniform vec4 uMap;
uniform vec3 uPts[32];   // source pixels + pressure
uniform int uN;
uniform float uR;        // source pixels
uniform float uFeather;
uniform float uOpacity;
uniform vec2 uOffset;    // source pixels (destination → source)
uniform int uClone;
uniform vec2 uOutOrigin;  // where this (patch-sized) output starts inside the uSrc texture, in texels
out vec4 o;
vec4 at(vec2 q) { return texture(uSrc, ((q - uMap.xy) / uMap.zw) / uTexSize); }
float strokeDist(vec2 q) {
  float dm = 1e9;
  for (int i = 0; i < 32; i++) {
    if (i >= uN) break;
    vec3 a = uPts[i];
    vec3 b = i + 1 < uN ? uPts[i + 1] : a;
    vec2 ab = b.xy - a.xy;
    float t = dot(ab, ab) > 1e-9 ? clamp(dot(q - a.xy, ab) / dot(ab, ab), 0.0, 1.0) : 0.0;
    dm = min(dm, length(q - (a.xy + t * ab)));
    if (i + 2 >= uN) break;
  }
  return dm;
}
float cover(vec2 q) {
  float d = strokeDist(q);
  float inner = uR * (1.0 - uFeather);
  return d <= inner ? 1.0 : d >= uR ? 0.0 : 1.0 - smoothstep(inner, uR, d);
}
// Hard disc at the full radius: the low-pass never sees anything the brush touched, feathered or not.
float outside(vec2 q) { return strokeDist(q) >= uR ? 1.0 : 0.0; }
// G(dst) − G(src) with one weight pattern for both, skipping taps that fall inside the destination stroke
// on either side: a linear gradient cancels exactly and the defect never leaks into either low-pass.
vec4 lowpassDiff(vec2 q) {
  float s = max(uR * 0.5, 0.5);
  vec4 acc = vec4(0.0), tot = vec4(0.0);
  float wsum = 0.0, asum = 0.0;
  for (int j = -5; j <= 5; j++)
    for (int i = -5; i <= 5; i++) {
      vec2 dv = vec2(float(i), float(j)) * s;
      float g = exp(-dot(dv, dv) / (2.0 * s * s));
      vec4 v = at(q + dv) - at(q + uOffset + dv);
      float keep = outside(q + dv) * outside(q + uOffset + dv);
      acc += g * keep * v;
      wsum += g * keep;
      tot += g * v;
      asum += g;
    }
  return wsum > 1e-4 ? acc / wsum : tot / asum;
}
void main() {
  vec2 q = uMap.xy + (gl_FragCoord.xy + uOutOrigin) * uMap.zw;
  vec4 d = at(q);
  float m = cover(q) * uOpacity;
  if (m <= 0.0) { o = d; return; }
  vec4 s = at(q + uOffset);
  vec4 v = uClone == 1 ? s : s + lowpassDiff(q);
  o = mix(d, vec4(v.rgb, s.a), m);
}`;

/** Separable Gaussian on RGBA (clamped edges), radius ≤ 128 taps. */
export const SKIN_BLUR = `${HEADER}
uniform sampler2D uSrc;
uniform vec2 uDir;
uniform float uSigma;
uniform int uR;
out vec4 o;
void main() {
  ivec2 p = ivec2(gl_FragCoord.xy);
  ivec2 sz = textureSize(uSrc, 0);
  vec4 acc = vec4(0.0);
  float ks = 0.0;
  for (int i = -128; i <= 128; i++) {
    if (i < -uR || i > uR) continue;
    float w = exp(-0.5 * float(i * i) / (uSigma * uSigma));
    acc += w * texelFetch(uSrc, clamp(p + ivec2(uDir) * i, ivec2(0), sz - 1), 0);
    ks += w;
  }
  o = acc / ks;
}`;

/**
 * Separable edge-preserving blur for the skin's low-frequency layer (CODE_REVIEW D29): Gaussian in space
 * × Gaussian on the encoded-luma difference (σ 0.15), so 10–40 px blotches flatten while the edges of eyes,
 * lips and hair (large luma steps) stay. Premultiplied or straight input both work (alpha rides along).
 */
export const SKIN_BILAT = `${HEADER}
uniform sampler2D uSrc;
uniform vec2 uDir;
uniform float uSigma;
uniform int uR;
uniform int uStep;
out vec4 o;
float encY(vec4 c) {
  float y = max(dot(c.rgb, vec3(0.2289746, 0.6917385, 0.0792869)), 0.0);
  return pow(y, 1.0 / 2.2);
}
void main() {
  ivec2 p = ivec2(gl_FragCoord.xy);
  ivec2 sz = textureSize(uSrc, 0);
  vec4 c0 = texelFetch(uSrc, p, 0);
  float y0 = encY(c0);
  vec4 acc = vec4(0.0);
  float ks = 0.0;
  for (int i = -uR; i <= uR; i += uStep) {
    vec4 c = texelFetch(uSrc, clamp(p + ivec2(uDir) * i, ivec2(0), sz - 1), 0);
    float d = encY(c) - y0;
    float w = exp(-0.5 * float(i * i) / (uSigma * uSigma) - 0.5 * d * d / 0.0225);
    acc += w * c;
    ks += w;
  }
  o = acc / ks;
}`;

/** out = mix(img, smooth + (img − low), mask · amount) — texture (img − low) is kept (§11.10). */
export const SKIN_COMBINE = `${HEADER}
uniform sampler2D uImg;
uniform sampler2D uLow;
uniform sampler2D uSmooth;
uniform sampler2D uMask;     // skin raster over the whole source
uniform mat3 uToSrc;         // target pixel → source normalized
uniform float uAmount;       // 0..100
out vec4 o;
void main() {
  ivec2 p = ivec2(gl_FragCoord.xy);
  vec4 img = texelFetch(uImg, p, 0);
  vec3 h = uToSrc * vec3(gl_FragCoord.xy, 1.0);
  float m = texture(uMask, h.xy / h.z).r * uAmount / 100.0;
  vec4 v = texelFetch(uSmooth, p, 0) + img - texelFetch(uLow, p, 0);
  o = vec4(mix(img.rgb, v.rgb, clamp(m, 0.0, 1.0)), img.a);
}`;

/** Red 50% overlay of one mask over the image rect on the canvas. */
export const MASK_OVERLAY = `${HEADER}${MASK_FNS}
uniform mat3 uOutToSrc;
uniform vec2 uOrigin;
uniform vec2 uOutSize;
uniform int uIndex;
out vec4 o;
void main() {
  vec2 fc = gl_FragCoord.xy - uOrigin;
  fc.y = uOutSize.y - fc.y;
  vec3 h = uOutToSrc * vec3(fc, 1.0);
  vec2 uv = h.xy / h.z;
  if (uv.x < 0.0 || uv.y < 0.0 || uv.x > 1.0 || uv.y > 1.0) discard;
  float w = maskW(uIndex, uv);
  o = vec4(1.0, 0.0, 0.0, 0.5 * w);
}`;
